import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import BopExcelTable, { BopExcelColumn } from '../../../components/BopExcelTable';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { formatCurrency } from '../../../utils/formatters';
import { API_DOCS } from './ScrittureContabiliPage.api';
import './Scritture.css';

const MODULO = 'amministrazione.scritture_contabili';

type Stato = 'BOZZA' | 'CONFERMATA' | 'STORNATA';

const STATO_LABEL: Record<Stato, string> = { BOZZA: 'Bozza', CONFERMATA: 'Confermata', STORNATA: 'Stornata' };
const STATO_BADGE: Record<Stato, string> = { BOZZA: 'bop-badge--neutral', CONFERMATA: 'bop-badge--success', STORNATA: 'bop-badge--warning' };

interface RegistroCombo { id: number; codice: string; descrizione: string; tipo: string; attivo: boolean }
interface CausaleCombo { id: number; codice: string; descrizione: string; tipo: string; gestisceIva: boolean; attivo: boolean }
interface NomCombo { id: number; codice: string; ragioneSociale: string | null; nome: string | null; cognome: string | null }

interface Scrittura {
  id: number;
  societaId: number;
  registroContabile: { id: number; codice: string; descrizione: string; tipo: string } | null;
  numero: number | null;
  anno: number | null;
  data: string;
  causale: { id: number; codice: string; descrizione: string; tipo: string; gestisceIva: boolean } | null;
  sezionaleIva: { id: number; codice: string; descrizione: string } | null;
  numeroSezIva: number | null;
  annoSezIva: number | null;
  nominativo: { id: number; codice: string; ragioneSociale: string | null; nome: string | null; cognome: string | null } | null;
  numeroDocumento: string | null;
  descrizione: string;
  importoTotale: number;
  stato: Stato;
  scritturaStornataId: number | null;
  scritturaStornoId: number | null;
}

function nominativoLabel(n: { ragioneSociale?: string | null; nome?: string | null; cognome?: string | null; codice?: string } | null): string {
  if (!n) return '';
  if (n.ragioneSociale) return n.ragioneSociale;
  const np = [n.nome, n.cognome].filter(Boolean).join(' ').trim();
  return np || n.codice || '';
}

export default function ScrittureContabiliPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');
  const canDelete = can(MODULO, 'delete');
  const navigate = useNavigate();

  const { societaId, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  const [items, setItems] = useState<Scrittura[]>([]);
  const [registri, setRegistri] = useState<RegistroCombo[]>([]);
  const [causali, setCausali] = useState<CausaleCombo[]>([]);
  const [nominativi, setNominativi] = useState<NomCombo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [ricerca, setRicerca] = useState('');
  const [filtroRegistro, setFiltroRegistro] = useState<string>('');
  const [filtroCausale, setFiltroCausale] = useState<string>('');
  const [filtroNominativo, setFiltroNominativo] = useState<string>('');
  const [filtroStato, setFiltroStato] = useState<'' | Stato>('');
  const [dataDal, setDataDal] = useState('');
  const [dataAl, setDataAl] = useState('');

  const load = async (silent = false) => {
    if (!societaId) { setItems([]); setRegistri([]); setCausali([]); setNominativi([]); setLoading(false); return; }
    if (!silent) setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ societaId: String(societaId) });
      if (filtroRegistro) qs.append('registroId', filtroRegistro);
      if (filtroCausale) qs.append('causaleId', filtroCausale);
      if (filtroNominativo) qs.append('nominativoId', filtroNominativo);
      if (filtroStato) qs.append('stato', filtroStato);
      if (dataDal) qs.append('dataDal', dataDal);
      if (dataAl) qs.append('dataAl', dataAl);
      if (ricerca.trim()) qs.append('q', ricerca.trim());
      // @api GET /api/scritture-contabili?societaId=&registroId=&causaleId=&nominativoId=&stato=&dataDal=&dataAl=&q= -> { success: true, data: [{ id, registroContabile, numero, anno, data, causale, sezionaleIva, nominativo, descrizione, importoTotale, stato }] }
      const res = await apiFetch<{ success: boolean; data: Scrittura[] }>(`/api/scritture-contabili?${qs.toString()}`);
      setItems(res.data ?? []);

      if (!silent) {
        // Carica combo una sola volta per società
        // @api GET /api/registri-contabili?societaId= -> { success: true, data: [...] }
        const resR = await apiFetch<{ success: boolean; data: RegistroCombo[] }>(`/api/registri-contabili?societaId=${societaId}`);
        // @api GET /api/causali-contabili?societaId= -> { success: true, data: [...] }
        const resC = await apiFetch<{ success: boolean; data: CausaleCombo[] }>(`/api/causali-contabili?societaId=${societaId}`);
        // @api GET /api/nominativi?societaId= -> [{ id, codice, ragioneSociale, nome, cognome }]
        const resN = await apiFetch<NomCombo[] | { data: NomCombo[] }>(`/api/nominativi?societaId=${societaId}`);
        const nomList = Array.isArray(resN) ? resN : ((resN as any).data || []);
        setRegistri((resR.data ?? []).filter(r => r.attivo));
        setCausali((resC.data ?? []).filter(c => c.attivo));
        setNominativi(nomList);
      }
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(false); }, [societaId]);

  // Ricarica silenziosa al cambio filtri
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (societaId) load(true); }, [filtroRegistro, filtroCausale, filtroNominativo, filtroStato, dataDal, dataAl, ricerca]);

  const eliminaBozza = async (s: Scrittura) => {
    if (!window.confirm(`Eliminare la BOZZA "${s.descrizione}"?`)) return;
    try {
      // @api DELETE /api/scritture-contabili/:id -> { success: true }
      await apiFetch(`/api/scritture-contabili/${s.id}`, { method: 'DELETE' });
      load(true);
    } catch (e: any) {
      window.alert(e.message || 'Errore');
    }
  };

  const stornaScrittura = async (s: Scrittura) => {
    const dataDef = new Date().toISOString().slice(0, 10);
    const dataInput = window.prompt(`Data dello storno (default oggi ${dataDef}):`, dataDef);
    if (!dataInput) return;
    try {
      // @api PATCH /api/scritture-contabili/:id/storna { data } -> { success: true, data: { id, numero, anno } }
      const res = await apiFetch<{ success: boolean; data: { id: number; numero: number; anno: number } }>(
        `/api/scritture-contabili/${s.id}/storna`,
        { method: 'PATCH', body: JSON.stringify({ data: dataInput }) },
      );
      window.alert(`Storno generato come scrittura #${res.data.id} (numero ${res.data.numero}/${res.data.anno})`);
      load(true);
    } catch (e: any) {
      window.alert(e.message || 'Errore nello storno');
    }
  };

  const noSocieta = !societaId;
  const totaleVisualizzati = items.length;
  const totaleImporto = useMemo(() => items.reduce((s, x) => s + (x.stato !== 'STORNATA' ? Number(x.importoTotale) : 0), 0), [items]);

  const columns = useMemo<BopExcelColumn<Scrittura>[]>(() => [
    {
      key: 'data',
      header: 'Data',
      accessor: (s) => s.data || '',
      filter: { type: 'dateRange' },
      width: '110px',
      cellClassName: 'sc-data',
      render: (s) => s.data ? new Date(s.data).toLocaleDateString('it-IT') : '—',
    },
    {
      key: 'numero',
      header: 'N° / Anno',
      accessor: (s) => s.numero != null ? `${s.numero}/${s.anno}` : '',
      filter: { type: 'text' },
      width: '110px',
      cellClassName: 'sc-numero',
      render: (s) => (
        <>
          {s.numero != null ? `${s.numero}/${s.anno}` : <span className="bop-text-muted">—</span>}
          {s.numeroSezIva != null && <div className="sc-num-iva">IVA: {s.numeroSezIva}/{s.annoSezIva}</div>}
        </>
      ),
    },
    {
      key: 'registro',
      header: 'Registro',
      accessor: (s) => s.registroContabile?.codice || '',
      filter: { type: 'text' },
      width: '130px',
      render: (s) => s.registroContabile ? <strong>{s.registroContabile.codice}</strong> : '—',
    },
    {
      key: 'causale',
      header: 'Causale',
      accessor: (s) => s.causale?.codice || '',
      filter: { type: 'text' },
      width: '130px',
      render: (s) => s.causale
        ? <span className="sc-causale">{s.causale.codice}</span>
        : <span className="bop-text-muted">libera</span>,
    },
    {
      key: 'descrizione',
      header: 'Descrizione',
      accessor: (s) => `${s.descrizione} ${s.numeroDocumento || ''}`.trim(),
      filter: { type: 'text' },
      render: (s) => (
        <>
          <div className="sc-desc">{s.descrizione}</div>
          {s.numeroDocumento && <div className="sc-doc">doc. {s.numeroDocumento}</div>}
        </>
      ),
    },
    {
      key: 'nominativo',
      header: 'Nominativo',
      accessor: (s) => nominativoLabel(s.nominativo),
      filter: { type: 'text' },
      render: (s) => nominativoLabel(s.nominativo) || <span className="bop-text-muted">—</span>,
    },
    {
      key: 'importo',
      header: 'Importo',
      accessor: (s) => Number(s.importoTotale),
      filter: { type: 'number' },
      width: '140px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right sc-importo',
      render: (s) => formatCurrency(s.importoTotale),
    },
    {
      key: 'stato',
      header: 'Stato',
      accessor: (s) => s.stato,
      filter: {
        type: 'enum',
        options: [
          { value: 'BOZZA',      label: 'Bozza' },
          { value: 'CONFERMATA', label: 'Confermata' },
          { value: 'STORNATA',   label: 'Stornata' },
        ],
      },
      width: '130px',
      render: (s) => (
        <>
          <span className={`bop-badge ${STATO_BADGE[s.stato]}`}>{STATO_LABEL[s.stato]}</span>
          {s.scritturaStornataId && <div className="sc-storno-info">storno di #{s.scritturaStornataId}</div>}
        </>
      ),
    },
  ], []);

  return (
    <Layout>
      <div className="scritture-contabili">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>Scritture contabili</h2>
            <p>Registrazioni in partita doppia — manuali o generate da causale, con numerazione progressiva e gestione stati</p>
          </div>
          {canEdit && (
            <div className="bop-flex">
              <button className="bop-btn bop-btn-primary" onClick={() => navigate('/amministrazione/scritture-contabili/nuova')} disabled={noSocieta}>
                + Nuova scrittura
              </button>
            </div>
          )}
        </div>

        <div className="bop-filter-bar sc-filter-bar">
          {!isUnica && (
            <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
              <option value="">Seleziona società…</option>
              {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          )}
          <input className="bop-input sc-search" placeholder="Cerca descrizione o doc…" value={ricerca} onChange={e => setRicerca(e.target.value)} />
          <select className="bop-select" value={filtroRegistro} onChange={e => setFiltroRegistro(e.target.value)}>
            <option value="">Tutti i registri</option>
            {registri.map(r => <option key={r.id} value={r.id}>{r.codice} — {r.descrizione}</option>)}
          </select>
          <select className="bop-select" value={filtroCausale} onChange={e => setFiltroCausale(e.target.value)}>
            <option value="">Tutte le causali</option>
            {causali.map(c => <option key={c.id} value={c.id}>{c.codice} — {c.descrizione}</option>)}
          </select>
          <select className="bop-select" value={filtroStato} onChange={e => setFiltroStato(e.target.value as any)}>
            <option value="">Tutti gli stati</option>
            <option value="BOZZA">Bozza</option>
            <option value="CONFERMATA">Confermata</option>
            <option value="STORNATA">Stornata</option>
          </select>
          <input className="bop-input sc-date" type="date" value={dataDal} onChange={e => setDataDal(e.target.value)} placeholder="dal" title="Data dal" />
          <input className="bop-input sc-date" type="date" value={dataAl} onChange={e => setDataAl(e.target.value)} placeholder="al" title="Data al" />
        </div>

        {error && <div className="bop-inline-error">{error}</div>}

        {noSocieta ? (
          <div className="bop-card">
            <div className="bop-empty-state">Seleziona una società per vedere le scritture.</div>
          </div>
        ) : (
          <>
            <BopExcelTable<Scrittura>
              data={items}
              loading={loading}
              rowKey={(s) => s.id}
              onRowClick={(s) => navigate(`/amministrazione/scritture-contabili/${s.id}`)}
              initialSort={{ key: 'data', dir: 'desc' }}
              emptyState={<div className="bop-empty-state">Nessuna scrittura per i filtri impostati.</div>}
              columns={columns}
              rowActions={(s) => (
                <BopRowActions actions={[
                  { icon: <RowIcon.Edit />, label: 'Modifica', onClick: () => navigate(`/amministrazione/scritture-contabili/${s.id}`), hidden: !canEdit || s.stato === 'STORNATA' },
                  { icon: <RowIcon.Disable />, label: 'Storna', onClick: () => stornaScrittura(s), hidden: !canEdit || s.stato !== 'CONFERMATA' },
                  { icon: <RowIcon.Trash />, label: 'Elimina', onClick: () => eliminaBozza(s), variant: 'danger', hidden: !canDelete || s.stato !== 'BOZZA' },
                ]} />
              )}
            />
            {items.length > 0 && (
              <div className="sc-totale-row-line">
                <span>Totale visualizzati ({totaleVisualizzati}):</span>
                <strong>{formatCurrency(totaleImporto)}</strong>
              </div>
            )}
          </>
        )}
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
